import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState, createContext, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocaleCodes, PSEUDO_LOCALES, SITE_LOCALES } from '@medable/i18n-supported-locales';
import { Locale } from 'libs.i18n-core';
import { LocaleContext, ModalWindow, SetLocaleContext, Spinner, useLocalizeMessage } from 'libs.nucleus';
import { useBooleanState } from 'libs.react';
import { formatLocaleOption, LocalesDropdown } from 'libs.react_internal';
import { updateMyAccountDetails } from '../../helpers/axon_user.helpers';
import { getUserLocaleLocalStorageKey } from '../../helpers/local_storage.helpers';
import { isPseudoLocale, toCortexLocale } from '../../helpers/pseudo_locale.helpers';
import { parseCortexUrl } from '../../helpers/study.helpers';
import { resolveStudyLocale } from '../../helpers/study_locale.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { AxonAccountContext } from '../axon_account';
import { SiteContext } from '../site';
const includesLocale = (locales, locale) => locales.some((supportedLocale) => toCortexLocale(supportedLocale) === toCortexLocale(locale));
//  Dedupe by normalized locale, keeping the first occurrence, so format variants (e.g. `en-US` and `en_US`) don't repeat.
const dedupeLocales = (locales) => {
    const seen = new Set();
    return locales.filter((locale) => {
        const key = toCortexLocale(locale);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
};
const isPseudoLocaleEnabledEnvironment = (environment) => {
    if (!environment?.cortexURL) {
        return false;
    }
    const parsedCortexUrl = parseCortexUrl(environment.cortexURL);
    return ['int-dev', 'qa', 'test', 'local'].includes(parsedCortexUrl?.env || '');
};
const waitForLocaleReloadBoundary = () => new Promise((resolve) => setTimeout(resolve, 0));
const getSupportedLocale = (locales, locale) => locales.find((supportedLocale) => !!locale && toCortexLocale(supportedLocale) === toCortexLocale(locale));
export const UserLocaleContext = createContext({
    isUserLocaleChecked: false,
    siteLocales: [],
    openLocaleModal: () => { },
    getUserSelectedLocale: () => '',
});
export const UserLocaleProvider = ({ children, rootShellLocale, setShellLocale, }) => {
    const { environment } = useCurrentEnvironment();
    const currentLocale = useContext(LocaleContext);
    const { site } = useContext(SiteContext);
    //  English (en-US) is the base locale and is always offered as a selectable option for site users. Only prepend it
    //  when the site doesn't already support it (in any format) so we keep the site's own value and avoid duplicates.
    const configuredLocales = useMemo(() => dedupeLocales(site?.c_site_supported_locales ?? [...SITE_LOCALES]), [site]);
    const baseSiteLocalesList = useMemo(() => includesLocale(configuredLocales, LocaleCodes.en_US)
        ? configuredLocales
        : [LocaleCodes.en_US, ...configuredLocales], [configuredLocales]);
    const siteLocalesList = useMemo(() => isPseudoLocaleEnabledEnvironment(environment)
        ? dedupeLocales([...baseSiteLocalesList, ...PSEUDO_LOCALES])
        : baseSiteLocalesList, [baseSiteLocalesList, environment]);
    const navigate = useNavigate();
    const [isLocaleModalOpen, setIsLocaleModalOpen, setIsLocaleModalClosed] = useBooleanState();
    const [selectedLocale, setSelectedLocale] = useState();
    const [isUserLocaleChecked, setIsUserLocaleChecked] = useState(false);
    const [isLoading, setIsLoading, setIsNotLoading] = useBooleanState();
    const { account: axonUser } = useContext(AxonAccountContext);
    const translate = useLocalizeMessage();
    const setLocale = useContext(SetLocaleContext);
    // SetLocaleContext's value is a fresh closure each render; keep it in a ref so the unmount cleanup
    // below can call the latest one without re-subscribing the effect.
    const setLocaleRef = useRef(setLocale);
    setLocaleRef.current = setLocale;
    const setShellLocaleRef = useRef(setShellLocale);
    setShellLocaleRef.current = setShellLocale;
    // The root Sites locale to restore when leaving study routes (captured at mount, before any
    // study-scoped selection). Prevents a study locale from leaking into the dashboard/training.
    const rootLocaleRef = useRef(currentLocale);
    const rootShellLocaleRef = useRef(rootShellLocale ?? currentLocale);
    const userLocaleKey = getUserLocaleLocalStorageKey({
        environmentId: environment?.id || 0,
        siteId: site?._id || '',
        axonUserId: axonUser?._id || '',
    });
    const setUserLocale = async (locale) => {
        if (!environment || !axonUser) {
            return;
        }
        if (!getSupportedLocale(siteLocalesList, locale)) {
            openLocaleModal();
            return;
        }
        setIsLoading();
        try {
            // Pseudo locale updates skip API calls, so yield once to let the loading/unmount boundary render.
            await waitForLocaleReloadBoundary();
            // The study language picker is site-scoped: a real locale updates the Axon account (and
            // localStorage); a pseudo locale is localStorage-only. It must NOT touch IAM/profile — the
            // global preferred language stays owned by the profile/onboarding flows.
            if (!isPseudoLocale(locale)) {
                await updateMyAccountDetails({
                    environment,
                    accountId: axonUser?._id,
                    payload: { locale },
                });
            }
            localStorage.setItem(userLocaleKey, locale);
            const localeToApply = Locale[toCortexLocale(locale)];
            setLocale?.(localeToApply);
            setShellLocaleRef.current?.(localeToApply);
            setSelectedLocale(formatLocaleOption(locale, locale));
            setIsUserLocaleChecked(true);
        }
        catch (error) {
            //  If persisting the locale fails we must not leave the app stranded behind the full-screen
            //  spinner (isUserLocaleChecked stays false and no modal is open). Fall back to the locale
            //  picker so the user can choose/retry instead of an indefinite loading state.
            console.error('Failed to persist user locale', error);
            openLocaleModal();
        }
        finally {
            setIsNotLoading();
        }
    };
    const checkUserLocale = async () => {
        const storedLocale = localStorage.getItem(userLocaleKey);
        const resolution = resolveStudyLocale({
            siteLocales: siteLocalesList,
            storedLocale,
            axonLocale: axonUser?.locale,
            rootLocale: currentLocale,
        });
        if (resolution.action === 'clear-and-prompt') {
            //  The stored site locale is no longer supported by this site; drop it and force a re-selection.
            localStorage.removeItem(userLocaleKey);
            setIsUserLocaleChecked(false);
            openLocaleModal();
            return;
        }
        if (resolution.action === 'prompt') {
            openLocaleModal();
            return;
        }
        //  Already active and persisted — nothing to apply.
        if (storedLocale &&
            toCortexLocale(storedLocale) === toCortexLocale(resolution.locale) &&
            toCortexLocale(resolution.locale) === toCortexLocale(currentLocale)) {
            setIsUserLocaleChecked(true);
            return;
        }
        await setUserLocale(resolution.locale);
    };
    useEffect(() => {
        if (environment && axonUser && site) {
            checkUserLocale();
        }
    }, [environment, axonUser, site, currentLocale, siteLocalesList, userLocaleKey]);
    //  On leaving study routes (unmount), restore the root Sites/shell locale so a study-scoped locale
    //  does not persist into the dashboard/training.
    useEffect(() => () => {
        setLocaleRef.current?.(rootLocaleRef.current);
        setShellLocaleRef.current?.(rootShellLocaleRef.current);
    }, []);
    const updateUserLocale = async () => {
        const resolvedLocale = selectedLocale ?? selectedLocaleValue;
        if (!environment || !axonUser || !resolvedLocale) {
            return;
        }
        const locale = isPseudoLocale(resolvedLocale.value) ? resolvedLocale.value : toCortexLocale(resolvedLocale.value);
        // Temporarily unmount children to force re-render with new locale
        setIsUserLocaleChecked(false);
        await setUserLocale(locale);
        setIsLocaleModalClosed();
    };
    const getUserSelectedLocale = () => {
        const userEnvironmentSelectedLocale = localStorage.getItem(userLocaleKey);
        return userEnvironmentSelectedLocale ?? axonUser?.locale ?? currentLocale;
    };
    const getSupportedSelectedLocale = () => {
        const selectedLocaleValue = selectedLocale?.value;
        const selectedSupportedLocale = getSupportedLocale(siteLocalesList, selectedLocaleValue);
        if (selectedSupportedLocale) {
            return formatLocaleOption(selectedSupportedLocale, selectedSupportedLocale);
        }
        const userSelectedLocale = getSupportedLocale(siteLocalesList, getUserSelectedLocale());
        return userSelectedLocale ? formatLocaleOption(userSelectedLocale, userSelectedLocale) : undefined;
    };
    const onModalClose = () => {
        setIsLocaleModalClosed();
        if (!isUserLocaleChecked) {
            navigate('/site/dashboard');
        }
    };
    const openLocaleModal = () => {
        setIsLocaleModalOpen();
    };
    const value = useMemo(() => ({
        isUserLocaleChecked,
        siteLocales: siteLocalesList,
        openLocaleModal,
        getUserSelectedLocale,
    }), [isUserLocaleChecked, siteLocalesList, openLocaleModal, getUserSelectedLocale]);
    const selectedLocaleValue = getSupportedSelectedLocale();
    return (_jsxs(UserLocaleContext.Provider, { value: value, children: [(isLoading || !axonUser || !site || (!isUserLocaleChecked && !isLocaleModalOpen)) && _jsx(Spinner, { wrapper: 'full' }), isUserLocaleChecked && children, _jsx(ModalWindow, { title: translate('generic.language'), isOpen: isLocaleModalOpen, closeWindow: () => { }, footerPrimaryActionButton: {
                    label: translate('generic.save'),
                    onClick: updateUserLocale,
                    disabled: !selectedLocaleValue,
                }, footerSecondaryActionButtons: [{ label: translate('generic.cancel'), onClick: onModalClose }], width: 'sm', children: _jsx(LocalesDropdown, { locales: siteLocalesList, label: translate('generic.language'), currentLocale: currentLocale, value: selectedLocaleValue, onChange: (value) => {
                        setSelectedLocale(value);
                    } }) })] }));
};
